import React, { useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBackIosNewRounded as ArrowBackIosNewIcon,
  ChevronRightRounded as ChevronRightIcon,
  WidgetsRounded as WidgetsIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import colors from '../../../assets/theme/colors';
import { getStructuredTrackingGroups } from './structuredTrackingActions';
import CustomPreparationStatusList from '../../../features/preparationJourneys/CustomPreparationStatusList';

const ACTION_HANDLER_PROPS = [
  'onOpenCareKnowledge',
  'onOpenInsights',
  'onOpenTherapyPrep',
  'onOpenCustomPrepare',
  'onOpenIepJourney',
  'onOpenSleepLog',
  'onOpenBathroomLog',
  'onOpenFoodLog',
  'onOpenMedicalLog',
  'onOpenMood',
  'onOpenIncident',
  'onOpenCustomNote',
  'onImportLogs',
];

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      mt: 1.5,
      mb: 0.55,
      px: 0.3,
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: alpha(colors.semantic.ink, 0.62),
    }}
  >
    {children}
  </Typography>
);

const ActionRow = ({ action, onClick, focusColor }) => {
  const IconComponent = action.icon;
  const palette = action.palette || {};
  const ink = palette.text || colors.brand.deep;
  const bg = palette.softBg || alpha(colors.brand.tint, 0.08);
  const iconBg = '#FFFFFF';

  return (
    <ButtonBase
      onClick={onClick}
      aria-label={action.label}
      sx={{
        width: '100%',
        minHeight: 58,
        justifyContent: 'flex-start',
        gap: 1.2,
        px: 1,
        py: 0.95,
        borderRadius: '18px',
        bgcolor: bg,
        color: ink,
        border: `1px solid ${alpha(ink, 0.12)}`,
        transition: 'background-color 140ms ease, transform 140ms ease',
        '&:hover': {
          bgcolor: alpha(ink, 0.08),
          transform: 'translateY(-1px)',
        },
        '&:focus-visible': {
          outline: `2px solid ${focusColor || ink}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '12px',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          bgcolor: iconBg,
          color: ink,
        }}
      >
        <IconComponent sx={{ fontSize: 18, color: ink }} />
      </Box>
      <Typography sx={{ fontSize: '0.96rem', fontWeight: 650, color: ink, lineHeight: 1.15 }}>
        {action.label}
        {action.description ? (
          <Typography component="span" sx={{ display: 'block', mt: 0.35, fontSize: '0.78rem', fontWeight: 450, color: alpha(colors.semantic.ink, 0.66), lineHeight: 1.35 }}>
            {action.description}
          </Typography>
        ) : null}
      </Typography>
    </ButtonBase>
  );
};

const TOOL_SECTION_ACTION_IDS = new Set(['custom-prepare', 'patterns-trends']);

const StructuredTrackingLauncher = ({
  child,
  accent,
  label = 'Insights',
  compact = false,
  variant = 'launcher',
  askLauncher = null,
  sx = {},
  ...handlers
}) => {
  const theme = useTheme();
  const isMobileSheet = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const activeAccent = accent || {
    primary: colors.semantic.sageAction,
    ink: colors.semantic.sageActionInk,
    soft: colors.semantic.sageSoft,
    wash: colors.semantic.sageMist,
  };
  const getActionTone = (action, groupId) => {
    if (groupId === 'insights') {
      return {
        ...action,
        palette: {
          text: activeAccent.ink,
          softBg: alpha(activeAccent.primary, 0.1),
        },
      };
    }
    return action;
  };
  const groups = useMemo(() => (
    getStructuredTrackingGroups(child)
      .map((group) => ({
        ...group,
        actions: group.actions.filter((action) => typeof handlers[action.handler] === 'function'),
      }))
      .filter((group) => group.actions.length > 0)
  ), [child, handlers]);

  const handleActionClick = (action) => {
    setOpen(false);
    const handler = handlers[action.handler];
    if (action.id === 'custom-note') {
      handler?.(child, 'dailyLog');
      return;
    }
    handler?.(child);
  };

  if (!child || groups.length === 0) {
    return null;
  }

  if (variant === 'section') {
    const sectionGroups = groups
      .map((group) => ({
        ...group,
        actions: group.actions.filter((action) => TOOL_SECTION_ACTION_IDS.has(action.id)),
      }))
      .filter((group) => group.actions.length > 0);

    return (
      <Box
        sx={{
          p: { xs: 1.35, sm: 1.55 },
          borderRadius: '24px',
          bgcolor: colors.landing.surface,
          border: `1px solid ${colors.landing.borderLight}`,
          boxShadow: 'none',
          ...sx,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1.15 }}>
          <Box>
            <Typography sx={{ fontSize: { xs: '1.5rem', sm: '1.7rem' }, fontWeight: 950, color: colors.semantic.ink, lineHeight: 1 }}>
              Insights
            </Typography>
            <Typography sx={{ mt: 0.45, color: colors.landing.textMuted, fontSize: '0.9rem', lineHeight: 1.25 }}>
              Ask, prepare, and spot patterns.
            </Typography>
          </Box>
        </Box>

        {askLauncher ? (
          <Box sx={{ mb: 1.35 }}>
            {askLauncher}
          </Box>
        ) : null}

        <CustomPreparationStatusList childId={child.id} />

        {sectionGroups.map((group) => (
          <Box key={group.id}>
            <SectionLabel>{group.id === 'insights' ? 'Make & share' : group.title}</SectionLabel>
            <Stack spacing={0.75}>
              {group.actions.map((action) => (
                <ActionRow
                  key={action.id}
                  action={getActionTone(action, group.id)}
                  focusColor={activeAccent.primary}
                  onClick={() => handleActionClick(action)}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <>
      <ButtonBase
        onClick={() => setOpen(true)}
        aria-label="Open insights"
        sx={{
          width: '100%',
          minHeight: compact ? 52 : 58,
          px: compact ? 1 : 1.25,
          py: compact ? 0.6 : 1,
          borderRadius: compact ? '16px' : '16px',
          bgcolor: colors.semantic.profileFlow.cardBg,
          border: `1px solid ${alpha(colors.semantic.profileFlow.cardBorder, 0.9)}`,
          color: colors.semantic.ink,
          justifyContent: 'space-between',
          boxShadow: 'none',
          ...sx,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.9 : 0.85, minWidth: 0 }}>
          <Box
            sx={{
              width: compact ? 32 : 34,
              height: compact ? 32 : 34,
              borderRadius: compact ? '12px' : '11px',
              bgcolor: alpha(activeAccent.primary, compact ? 0.16 : 0.12),
              color: activeAccent.ink,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <WidgetsIcon sx={{ fontSize: compact ? 17 : 19 }} />
          </Box>
          <Typography sx={{ fontSize: compact ? '0.92rem' : '1rem', fontWeight: 700, color: colors.semantic.ink, lineHeight: 1 }}>
            {label}
          </Typography>
        </Box>
        <ChevronRightIcon sx={{ fontSize: 22, color: alpha(activeAccent.ink, 0.58), flexShrink: 0 }} />
      </ButtonBase>

      <Drawer
        anchor={isMobileSheet ? 'bottom' : 'right'}
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: isMobileSheet ? '100%' : { sm: 380 },
            height: isMobileSheet ? 'auto' : '100%',
            maxHeight: isMobileSheet ? '88vh' : '100vh',
            maxWidth: '100vw',
            bgcolor: colors.landing.surface,
            borderLeft: isMobileSheet ? 'none' : `1px solid ${colors.landing.borderLight}`,
            borderTop: isMobileSheet ? `1px solid ${colors.landing.borderLight}` : 'none',
            borderTopLeftRadius: isMobileSheet ? '28px' : 0,
            borderTopRightRadius: isMobileSheet ? '28px' : 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            overflow: 'hidden',
            boxShadow: isMobileSheet
              ? `0 -18px 48px ${colors.landing.shadowPanel}`
              : `-18px 0 48px ${colors.landing.shadowPanel}`,
          },
        }}
      >
        <Box sx={{ p: { xs: 2.2, sm: 2 }, pb: 2.5 }}>
          {isMobileSheet ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: -0.9, mb: 1.4 }}>
              <Box sx={{ width: 44, height: 5, borderRadius: 999, bgcolor: alpha(colors.semantic.ink, 0.16) }} />
            </Box>
          ) : null}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.55 }}>
            <IconButton
              aria-label="Close insights"
              onClick={() => setOpen(false)}
              sx={{
                width: 48,
                height: 48,
                borderRadius: '999px',
                bgcolor: alpha(colors.semantic.ink, 0.05),
                color: colors.semantic.ink,
                '&:hover': { bgcolor: alpha(colors.semantic.ink, 0.08) },
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '1.55rem', fontWeight: 900, color: colors.semantic.ink, lineHeight: 1.05 }}>
                Insights
              </Typography>
              <Typography sx={{ mt: 0.7, color: colors.landing.textMuted, fontSize: '0.98rem', lineHeight: 1.35 }}>
                Everything for {child?.name || 'care'}, in one place.
              </Typography>
            </Box>
          </Box>

          {groups.map((group) => (
            <Box key={group.id}>
              <SectionLabel>{group.title}</SectionLabel>
              <Stack spacing={0.75}>
                {group.actions.map((action) => (
                  <ActionRow
                    key={action.id}
                    action={getActionTone(action, group.id)}
                    focusColor={activeAccent.primary}
                    onClick={() => handleActionClick(action)}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  );
};

StructuredTrackingLauncher.defaultProps = ACTION_HANDLER_PROPS.reduce((props, key) => ({
  ...props,
  [key]: undefined,
}), {});

export default StructuredTrackingLauncher;
